import { useMemo } from 'react';
import { rage, useObservable } from '~/rage';
import { isRgbPaintType, VehiclePaintType } from '~/shared';
import { CategoryTypes } from '../components/CategoryTab';
import { useLayoutSelector } from '../redux';

export const usePaintColors = () => {
	const vehicleIndexedColors = useObservable(
		rage.vehiclePaintShop.getVehicleIndexedColors(),
	);
	const pearlColors = useObservable(
		rage.vehiclePaintShop.getVehiclePearlColors(),
	);
	const rimsColors = useObservable(
		rage.vehiclePaintShop.getVehicleRimsColors(),
	);
	const activePaintGroup = useLayoutSelector(
		(state) => state.colorPicker.paintGroup,
	);
	const sectionIndex = useLayoutSelector(
		(state) => state.colorPicker.sectionIndex,
	);
	const activeCategory = useLayoutSelector(
		(state) => state.carPaint.activeCategory,
	);
	const rimsColor = useLayoutSelector((state) => state.colorPicker.rimsColor);
	const indexedColor = useLayoutSelector(
		(state) => state.colorPicker.indexedColor,
	);
	const pearlColor = useLayoutSelector((state) => state.colorPicker.pearlColor);

	const rimsColorsExtended = useMemo(() => {
		return rimsColors.map((color) => ({
			...color,
			type: VehiclePaintType.Pearl,
		}));
	}, [rimsColors]);

	const colorList = useMemo(() => {
		const currentType = activePaintGroup?.type;
		const isPaintingWheels = activeCategory === CategoryTypes.PaintingWheels;

		if (isPaintingWheels) {
			return rimsColorsExtended;
		}

		if (!currentType) return [];

		const isPearl = currentType === VehiclePaintType.Pearl;

		if (isPearl) {
			return pearlColors;
		}

		if (isRgbPaintType(currentType)) {
			return [];
		}

		return vehicleIndexedColors.filter((color) => color.type === currentType);
	}, [
		activePaintGroup,
		vehicleIndexedColors,
		pearlColors,
		sectionIndex,
		activeCategory,
		rimsColorsExtended,
	]);

	const activeColor = useMemo(() => {
		const isPaintingWheels = activeCategory === CategoryTypes.PaintingWheels;

		if (isPaintingWheels) {
			return colorList.find((color) => color.index === rimsColor);
		}

		const currentType = activePaintGroup?.type;

		if (
			!currentType ||
			(isRgbPaintType(currentType) && currentType !== VehiclePaintType.Pearl)
		) {
			return null;
		}

		const isPearl = currentType === VehiclePaintType.Pearl;

		const colorIndex = isPearl ? pearlColor : indexedColor;

		return colorList.find(
			(color) => color.type === currentType && color.index === colorIndex,
		);
	}, [
		activePaintGroup,
		sectionIndex,
		colorList,
		rimsColor,
		activeCategory,
		pearlColor,
		indexedColor,
	]);

	return { colorList, activeColor };
};
